import React from 'react';
import { Box, Divider, List, ListItem, Typography } from '@mui/material';

import { BCRestraintSegments } from '@paragon/contract-library';
import { hasFreeSubscription, UpgradeNotification } from '@paragon/login-tools';
import { DistanceField } from '@paragon/material';

import { showUpgradeAccountDialog } from '../../../../../../store/actions/popupActions';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { MultiValueNumberInput } from '../../multi-value-controls/MultiValueNumberInput';
import { MultiValueSelectInput } from '../../multi-value-controls/MultiValueSelectInput';
import { LoadSettingsControlProps } from '../LoadSettingsControls';
import { DurationFactorSetting } from './DurationFactorSettings';
import { RestraintToggle } from './RestraintToggle';

export function DragLoadSettings({ disabled, loadSettingsArray, setLoadSettingsValues }: LoadSettingsControlProps) {
  const dispatch = useAppDispatch();

  // drag loads are not available to free users
  const isFreeUser = useAppSelector(hasFreeSubscription);
  if (isFreeUser) disabled = true;

  const onRestraintChangeDistance1 = (newValue: number, index: number) => {
    updateBCRestraint('startPoint', newValue, index);
  };

  const onRestraintChangeDistance2 = (newValue: number, index: number) => {
    updateBCRestraint('endPoint', newValue, index);
  };

  const updateBCRestraint = <K extends keyof BCRestraintSegments>(key: K, value: boolean | number, index: number) => {
    const newBCRestraints = loadSettingsArray[0].bcRestraintLocations!.slice();
    newBCRestraints[index] = {
      ...newBCRestraints[index],
      [key]: value,
    };

    setLoadSettingsValues({ bcRestraintLocations: newBCRestraints });
  };

  const dragRestraintList =
    loadSettingsArray.length === 1 ? (
      <>
        <Typography variant="h6" align="center" sx={{ mt: 2 }}>
          Bottom Chord Horizontal Restraint Locations
        </Typography>
        <Divider />
        {loadSettingsArray[0].bcRestraintLocations!.map(({ fromLeft, startPoint, endPoint }, index) => (
          <ListItem key={index}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-around', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RestraintToggle
                    disabled={disabled || loadSettingsArray[0].dragLoadTakenOutOnTopChord}
                    onUpdate={updateBCRestraint}
                    sx={{ ml: 2, transform: 'rotate(180deg)' }}
                    index={index}
                    fromLeft={fromLeft}
                  />
                </Box>
                <DistanceField
                  label="Start"
                  value={startPoint * 12} // This value is stored in feet
                  onSubmit={(value) => onRestraintChangeDistance1(value / 12, index)}
                  sx={{ mr: 2 }}
                  disabled={disabled || loadSettingsArray[0].dragLoadTakenOutOnTopChord}
                />
                <DistanceField
                  label="End"
                  value={endPoint * 12} // This value is stored in feet
                  onSubmit={(value) => onRestraintChangeDistance2(value / 12, index)}
                  sx={{ mr: 2 }}
                  disabled={disabled || loadSettingsArray[0].dragLoadTakenOutOnTopChord}
                />
              </Box>
            </Box>
          </ListItem>
        ))}
      </>
    ) : null;

  return (
    <Box sx={{ flexDirection: 'column' }}>
      {isFreeUser && (
        <UpgradeNotification
          sx={{ width: '100%', marginBottom: 1 }}
          briefDescription="drag loads"
          onUpgradeClick={() => dispatch(showUpgradeAccountDialog())}
        />
      )}

      <List dense>
        <ListItem>
          <MultiValueNumberInput
            values={loadSettingsArray.map((loadSettings) => loadSettings.totalDragLoad)}
            submit={(totalDragLoad) => setLoadSettingsValues({ totalDragLoad })}
            label="Total Drag Load"
            disabled={disabled}
          />
          <MultiValueSelectInput
            sx={{ ml: 2 }}
            property="Drag Load Unit"
            values={loadSettingsArray.map((loadSettings) => loadSettings.dragLoadUnit)}
            options={['Pounds', 'Plf'] as const}
            disabled={disabled}
            submit={(dragLoadUnit) => setLoadSettingsValues({ dragLoadUnit })}
          />
        </ListItem>
        <ListItem>
          <MultiValueSelectInput<'Top Chord' | 'Bottom Chord'>
            property="Chord to resist on"
            values={loadSettingsArray.map((loadSettings) =>
              loadSettings.dragLoadTakenOutOnTopChord ? 'Top Chord' : 'Bottom Chord',
            )}
            options={['Bottom Chord', 'Top Chord']}
            disabled={disabled}
            submit={(value) => {
              setLoadSettingsValues({ dragLoadTakenOutOnTopChord: value === 'Top Chord' });
            }}
          />
        </ListItem>
        {loadSettingsArray.some((loadSettings) => loadSettings.dragLoadTakenOutOnTopChord) ? (
          <ListItem>
            <MultiValueSelectInput<'Left' | 'Right'>
              property="Side to resist on (for top chord)"
              values={loadSettingsArray.map((loadSettings) =>
                loadSettings.dragLoadTakenOutLeftSide ? 'Left' : 'Right',
              )}
              options={['Left', 'Right']}
              disabled={disabled}
              submit={(value) => {
                setLoadSettingsValues({ dragLoadTakenOutLeftSide: value === 'Left' });
              }}
            />
          </ListItem>
        ) : null}
        <ListItem>
          <DurationFactorSetting
            sx={{ width: '100%' }}
            loadType="drag"
            loadSettingsArray={loadSettingsArray}
            setLoadSettingsValues={setLoadSettingsValues}
            disabled={disabled}
          />
        </ListItem>
      </List>
      {dragRestraintList}
    </Box>
  );
}
